import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SchoolIcon from "@mui/icons-material/School";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import DataObjectIcon from "@mui/icons-material/DataObject";
import HubIcon from "@mui/icons-material/Hub";
import DescriptionIcon from "@mui/icons-material/Description";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";

// Hero Section - 深色科技风格
function HeroSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      id="home"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #0a0a0f 0%, #12121a 50%, #0a0a0f 100%)",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        pt: { xs: 10, md: 0 },
        pb: { xs: 8, md: 0 },
      }}
    >
      {/* 背景动态效果 */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* 网格背景 */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backgroundImage:
              "linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        {/* 发光球体 */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "10%",
            width: { xs: 300, md: 500 },
            height: { xs: 300, md: 500 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, rgba(124, 58, 237, 0.1) 40%, transparent 70%)",
            filter: "blur(60px)",
            animation: "pulse 8s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 0.6, transform: "scale(1)" },
              "50%": { opacity: 1, transform: "scale(1.1)" },
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "20%",
            left: "5%",
            width: { xs: 200, md: 400 },
            height: { xs: 200, md: 400 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(124, 58, 237, 0.15) 0%, rgba(236, 72, 153, 0.1) 40%, transparent 70%)",
            filter: "blur(60px)",
            animation: "pulse 10s ease-in-out infinite reverse",
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              {/* 标签 */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  mb: 4,
                  borderRadius: "100px",
                  background: "rgba(0, 212, 255, 0.1)",
                  border: "1px solid rgba(0, 212, 255, 0.2)",
                }}
              >
                <ElectricBoltIcon sx={{ color: "#00d4ff", fontSize: 18 }} />
                <Typography
                  sx={{
                    color: "#00d4ff",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  AI驱动的智能知识库
                </Typography>
              </Box>

              {/* 主标题 */}
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  知嘛
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    color: "#f8fafc",
                    fontSize: { xs: "1.5rem", md: "2.25rem" },
                    fontWeight: 400,
                    mt: 2,
                    letterSpacing: "0",
                  }}
                >
                  让知识触手可及
                </Box>
              </Typography>

              {/* 副标题 */}
              <Typography
                variant="h6"
                sx={{
                  mb: 5,
                  color: "#94a3b8",
                  lineHeight: 1.8,
                  fontWeight: 400,
                  maxWidth: 500,
                }}
              >
                一款集AI深度搜索、文档分享、识字学习于一体的智能小程序，
                帮助您高效获取知识，轻松实现内容变现。
              </Typography>

              {/* 按钮组 */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 6 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RocketLaunchIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#0a0a0f",
                    background:
                      "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
                    boxShadow:
                      "0 0 30px rgba(0, 212, 255, 0.4), 0 4px 20px rgba(0, 212, 255, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
                      boxShadow:
                        "0 0 50px rgba(0, 212, 255, 0.5), 0 8px 30px rgba(0, 212, 255, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => {
                    document
                      .querySelector("#download")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  立即体验
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "#e2e8f0",
                    borderColor: "rgba(0, 212, 255, 0.4)",
                    borderWidth: 2,
                    "&:hover": {
                      borderColor: "#00d4ff",
                      borderWidth: 2,
                      bgcolor: "rgba(0, 212, 255, 0.1)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => {
                    document
                      .querySelector("#features")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  了解更多
                </Button>
              </Box>

              {/* 统计数据 */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 4, md: 6 },
                  flexWrap: "wrap",
                }}
              >
                {[
                  { value: "10万+", label: "活跃用户" },
                  { value: "50万+", label: "文档资源" },
                  { value: "99.9%", label: "服务可用" },
                ].map((stat) => (
                  <Box key={stat.label}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        background:
                          "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* 手机模型 */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: { xs: 260, md: 300 },
                  height: { xs: 520, md: 600 },
                  background:
                    "linear-gradient(145deg, #1a1a24 0%, #0a0a0f 100%)",
                  borderRadius: "44px",
                  padding: "14px",
                  boxShadow:
                    "0 30px 60px rgba(0, 0, 0, 0.5), 0 0 50px rgba(0, 212, 255, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  animation: "float 6s ease-in-out infinite",
                  "@keyframes float": {
                    "0%, 100%": { transform: "translateY(0)" },
                    "50%": { transform: "translateY(-15px)" },
                  },
                }}
              >
                {/* 刘海 */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 14,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 100,
                    height: 30,
                    bgcolor: "#0a0a0f",
                    borderRadius: "0 0 20px 20px",
                    zIndex: 10,
                  }}
                />

                {/* 屏幕内容 */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    background: "#ffffff",
                    borderRadius: "34px",
                    overflow: "hidden",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* App Header */}
                  <Box
                    sx={{
                      py: 4,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderBottom: "1px solid #f0f0f0",
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        background:
                          "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      知嘛
                    </Typography>
                  </Box>

                  {/* 搜索框 */}
                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        background: "#f5f5f5",
                        border: "1px solid #e5e5e5",
                        borderRadius: "12px",
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <SearchIcon sx={{ color: "#00d4ff", fontSize: 20 }} />
                      <Typography sx={{ color: "#999", fontSize: 13 }}>
                        请把你想知道的告诉我
                      </Typography>
                    </Box>
                  </Box>

                  {/* 深度搜索卡片 */}
                  <Box sx={{ px: 2, py: 1 }}>
                    <Box
                      sx={{
                        background:
                          "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                        borderRadius: "12px",
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <SmartToyIcon sx={{ color: "#ffffff", fontSize: 18 }} />
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: "#ffffff",
                          }}
                        >
                          深度搜索
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "rgba(255,255,255,0.8)",
                          mt: 0.5,
                        }}
                      >
                        AI智能搜索，精准匹配
                      </Typography>
                    </Box>
                  </Box>

                  {/* 功能网格 */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 1.5,
                      p: 2,
                    }}
                  >
                    {[
                      {
                        icon: <AutoStoriesIcon sx={{ fontSize: 18 }} />,
                        color: "#00d4ff",
                        bg: "rgba(0, 212, 255, 0.1)",
                      },
                      {
                        icon: <VideoLibraryIcon sx={{ fontSize: 18 }} />,
                        color: "#7c3aed",
                        bg: "rgba(124, 58, 237, 0.1)",
                      },
                      {
                        icon: <SchoolIcon sx={{ fontSize: 18 }} />,
                        color: "#10b981",
                        bg: "rgba(16, 185, 129, 0.1)",
                      },
                      {
                        icon: <CloudUploadIcon sx={{ fontSize: 18 }} />,
                        color: "#ec4899",
                        bg: "rgba(236, 72, 153, 0.1)",
                      },
                    ].map((item, i) => (
                      <Box
                        key={i}
                        sx={{
                          bgcolor: item.bg,
                          borderRadius: "12px",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: item.color,
                        }}
                      >
                        {item.icon}
                      </Box>
                    ))}
                  </Box>

                  {/* 热门列表 */}
                  <Box sx={{ px: 2, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#333",
                        mb: 1.5,
                      }}
                    >
                      热门推荐
                    </Typography>
                    {[1, 2, 3].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          py: 1.5,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            background:
                              "linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <DescriptionIcon
                            sx={{ fontSize: 16, color: "#00d4ff" }}
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            height: 10,
                            bgcolor: "#f0f0f0",
                            borderRadius: 5,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// Features Section - 功能特色
function FeaturesSection() {
  const features = [
    {
      icon: <SmartToyIcon sx={{ fontSize: 36 }} />,
      title: "AI深度搜索",
      description:
        "基于大语言模型的智能搜索，理解您的真实意图，精准匹配相关内容，让信息获取更加高效。",
      gradient: "linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)",
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 36 }} />,
      title: "文档分享",
      description:
        "支持多种格式文档上传，轻松设置付费或免费分享，让您的知识产生价值。",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 36 }} />,
      title: "识字学习",
      description:
        "专业的汉字学习功能，配合语音朗读，帮助儿童和外国友人轻松学习中文。",
      gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    },
    {
      icon: <MonetizationOnIcon sx={{ fontSize: 36 }} />,
      title: "创作收益",
      description:
        "完善的收益系统，上传的付费文档被下载即可获得收益，支持便捷提现。",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    },
    {
      icon: <VideoLibraryIcon sx={{ fontSize: 36 }} />,
      title: "多媒体支持",
      description:
        "支持文档、视频、音频等多种格式的预览和播放，满足不同内容需求。",
      gradient: "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 36 }} />,
      title: "社交互动",
      description: "关注创作者、收藏内容、分享资源，建立知识社交网络。",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)",
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)",
        position: "relative",
      }}
    >
      {/* 背景装饰 */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          height: 800,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0, 212, 255, 0.05) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              mb: 3,
              borderRadius: "100px",
              background: "rgba(0, 212, 255, 0.1)",
              border: "1px solid rgba(0, 212, 255, 0.2)",
            }}
          >
            <AutoAwesomeIcon sx={{ color: "#00d4ff", fontSize: 18 }} />
            <Typography
              sx={{ color: "#00d4ff", fontSize: "0.875rem", fontWeight: 500 }}
            >
              核心功能
            </Typography>
          </Box>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: "#f8fafc",
            }}
          >
            功能特色
          </Typography>
          <Typography
            sx={{
              color: "#94a3b8",
              maxWidth: 600,
              mx: "auto",
              fontSize: "1.1rem",
            }}
          >
            知嘛小程序为您提供全方位的知识服务，从搜索到学习，从分享到变现
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: "rgba(18, 18, 26, 0.8)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                  borderRadius: "20px",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  overflow: "visible",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: "rgba(0, 212, 255, 0.3)",
                    boxShadow:
                      "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 212, 255, 0.1)",
                    "& .feature-icon": {
                      transform: "scale(1.1)",
                      boxShadow: "0 0 40px rgba(0, 212, 255, 0.4)",
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    className="feature-icon"
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "18px",
                      background: feature.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      color: "#0a0a0f",
                      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.3)",
                      transition: "all 0.4s ease",
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, mb: 2, color: "#f8fafc" }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#94a3b8",
                      lineHeight: 1.8,
                      fontSize: "0.95rem",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// Scenarios Section - 使用场景
function ScenariosSection() {
  const scenarios = [
    {
      title: "学生群体",
      description: "快速搜索学习资料、论文文献，下载课件笔记，提高学习效率",
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      features: ["资料搜索", "文档下载", "知识收藏"],
      gradient: "linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)",
    },
    {
      title: "内容创作者",
      description: "上传原创内容，设置付费下载，实现知识变现，获取稳定收益",
      icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
      features: ["内容上传", "定价销售", "收益提现"],
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
    },
    {
      title: "家长儿童",
      description: "使用识字学习功能，配合语音朗读，让孩子轻松快乐地学习汉字",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      features: ["识字卡片", "语音朗读", "学习记录"],
      gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    },
    {
      title: "职场人士",
      description: "获取行业报告、专业文档，提升职业技能，保持竞争力",
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      features: ["专业资料", "行业报告", "技能提升"],
      gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    },
  ];

  return (
    <Box
      id="scenarios"
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(180deg, #12121a 0%, #0a0a0f 100%)",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              mb: 3,
              borderRadius: "100px",
              background: "rgba(124, 58, 237, 0.1)",
              border: "1px solid rgba(124, 58, 237, 0.2)",
            }}
          >
            <HubIcon sx={{ color: "#a78bfa", fontSize: 18 }} />
            <Typography
              sx={{ color: "#a78bfa", fontSize: "0.875rem", fontWeight: 500 }}
            >
              应用场景
            </Typography>
          </Box>
          <Typography
            variant="h2"
            sx={{ fontWeight: 700, mb: 2, color: "#f8fafc" }}
          >
            使用场景
          </Typography>
          <Typography
            sx={{
              color: "#94a3b8",
              maxWidth: 600,
              mx: "auto",
              fontSize: "1.1rem",
            }}
          >
            无论您是学生、创作者还是职场人士，知嘛都能为您提供专属的知识服务
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {scenarios.map((scenario, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: "rgba(18, 18, 26, 0.6)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                  borderRadius: "24px",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: "rgba(124, 58, 237, 0.3)",
                    boxShadow:
                      "0 20px 50px rgba(0, 0, 0, 0.3), 0 0 40px rgba(124, 58, 237, 0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "20px",
                      background: scenario.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      color: "#0a0a0f",
                      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                    }}
                  >
                    {scenario.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, mb: 2, color: "#f8fafc" }}
                  >
                    {scenario.title}
                  </Typography>
                  <Typography sx={{ color: "#94a3b8", mb: 3, lineHeight: 1.8 }}>
                    {scenario.description}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {scenario.features.map((feature, i) => (
                      <Chip
                        key={i}
                        label={feature}
                        size="small"
                        sx={{
                          bgcolor: "rgba(0, 212, 255, 0.1)",
                          color: "#00d4ff",
                          border: "1px solid rgba(0, 212, 255, 0.2)",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// Why Choose Section - 为什么选择我们
function WhyChooseSection() {
  const advantages = [
    {
      icon: <SpeedIcon sx={{ fontSize: 32 }} />,
      title: "极速响应",
      description: "AI驱动搜索，毫秒级响应",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      title: "安全可靠",
      description: "数据加密，保护隐私",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      title: "持续更新",
      description: "功能迭代，体验优化",
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 32 }} />,
      title: "用心服务",
      description: "24/7客服，随时响应",
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        background:
          "linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 背景线条 */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          backgroundImage:
            "linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            为什么选择知嘛
          </Typography>
          <Typography
            sx={{
              color: "#64748b",
              maxWidth: 500,
              mx: "auto",
              fontSize: "1.1rem",
            }}
          >
            我们致力于打造最优质的知识服务平台
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {advantages.map((item, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box
                sx={{
                  textAlign: "center",
                  p: 4,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(0, 212, 255, 0.1)",
                  transition: "all 0.4s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: "rgba(0, 212, 255, 0.3)",
                    boxShadow:
                      "0 20px 40px rgba(0, 0, 0, 0.1), 0 0 30px rgba(0, 212, 255, 0.1)",
                    "& .icon-box": {
                      background:
                        "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                      color: "#0a0a0f",
                    },
                  },
                }}
              >
                <Box
                  className="icon-box"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "18px",
                    background: "rgba(0, 212, 255, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                    color: "#00d4ff",
                    transition: "all 0.4s ease",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 1, color: "#0f172a" }}
                >
                  {item.title}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// About Section - 关于我们
function AboutSection() {
  return (
    <Box
      id="about"
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                mb: 3,
                borderRadius: "100px",
                background: "rgba(16, 185, 129, 0.1)",
                border: "1px solid rgba(16, 185, 129, 0.2)",
              }}
            >
              <DataObjectIcon sx={{ color: "#10b981", fontSize: 18 }} />
              <Typography
                sx={{ color: "#10b981", fontSize: "0.875rem", fontWeight: 500 }}
              >
                关于我们
              </Typography>
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                background: "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              新知智能教育
            </Typography>
            <Typography sx={{ color: "#94a3b8", lineHeight: 2, mb: 3 }}>
              新知智能教育是一家专注于AI+教育领域的科技公司，致力于用人工智能技术改变传统的知识获取和学习方式。
            </Typography>
            <Typography sx={{ color: "#94a3b8", lineHeight: 2, mb: 3 }}>
              "知嘛"小程序是我们的核心产品之一，它将AI深度搜索、知识分享、在线学习等功能融为一体，为用户提供一站式的知识服务体验。
            </Typography>
            <Typography sx={{ color: "#94a3b8", lineHeight: 2, mb: 5 }}>
              我们的愿景是"让知识触手可及"，帮助每一个人更高效地获取知识，更轻松地实现成长。
            </Typography>
            <Box sx={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {[
                { value: "2023", label: "成立年份" },
                { value: "20+", label: "团队成员" },
                { value: "10万+", label: "服务用户" },
              ].map((stat) => (
                <Box key={stat.label}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 3,
              }}
            >
              {[
                { icon: "🎯", text: "专注创新" },
                { icon: "💡", text: "用户至上" },
                { icon: "🚀", text: "追求卓越" },
                { icon: "🤝", text: "合作共赢" },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    background: "rgba(18, 18, 26, 0.8)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    borderRadius: "20px",
                    p: 4,
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      borderColor: "rgba(0, 212, 255, 0.3)",
                      boxShadow: "0 0 30px rgba(0, 212, 255, 0.1)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 48, mb: 2 }}>
                    {item.icon}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#f8fafc",
                      fontSize: "1.1rem",
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// Download Section - 下载入口
function DownloadSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 微信小程序链接（URL Scheme）
  const miniProgramUrl = "weixin://dl/business/?appid=wx8ceff708a069392c";

  const handleOpenMiniProgram = () => {
    window.location.href = miniProgramUrl;
  };

  return (
    <Box
      id="download"
      sx={{
        py: { xs: 10, md: 14 },
        background: "linear-gradient(180deg, #12121a 0%, #0a0a0f 100%)",
        position: "relative",
      }}
    >
      {/* 背景发光 */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              mb: 3,
              borderRadius: "100px",
              background: "rgba(0, 212, 255, 0.1)",
              border: "1px solid rgba(0, 212, 255, 0.2)",
            }}
          >
            <PhoneIphoneIcon sx={{ color: "#00d4ff", fontSize: 18 }} />
            <Typography
              sx={{ color: "#00d4ff", fontSize: "0.875rem", fontWeight: 500 }}
            >
              立即体验
            </Typography>
          </Box>
          <Typography
            variant="h2"
            sx={{ fontWeight: 700, mb: 2, color: "#f8fafc" }}
          >
            开始使用知嘛
          </Typography>
          <Typography
            sx={{
              color: "#94a3b8",
              mb: 8,
              maxWidth: 500,
              mx: "auto",
              fontSize: "1.1rem",
            }}
          >
            {isMobile
              ? "点击下方按钮，即刻开启您的智能知识之旅"
              : "扫描下方小程序码，或在微信中搜索「知嘛」，即可开始您的智能知识之旅"}
          </Typography>

          {/* 移动端显示打开微信按钮 */}
          {isMobile ? (
            <Box sx={{ maxWidth: 400, mx: "auto" }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleOpenMiniProgram}
                startIcon={<PhoneIphoneIcon />}
                sx={{
                  py: 2,
                  px: 4,
                  mb: 3,
                  borderRadius: "16px",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#0a0a0f",
                  background:
                    "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
                  boxShadow:
                    "0 0 30px rgba(0, 212, 255, 0.4), 0 4px 20px rgba(0, 212, 255, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
                    boxShadow:
                      "0 0 50px rgba(0, 212, 255, 0.5), 0 8px 30px rgba(0, 212, 255, 0.4)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                打开微信小程序
              </Button>

              <Box
                sx={{
                  p: 4,
                  background: "rgba(18, 18, 26, 0.8)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                  borderRadius: "20px",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <SearchIcon sx={{ color: "#7c3aed", fontSize: 24 }} />
                  <Typography sx={{ color: "#e2e8f0", fontWeight: 500 }}>
                    或在微信中搜索
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    background:
                      "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  "知嘛"
                </Typography>
              </Box>
            </Box>
          ) : (
            /* 桌面端显示二维码和搜索 */
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} sm={6} md={5}>
                <Box
                  sx={{
                    p: 5,
                    background: "rgba(18, 18, 26, 0.8)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    borderRadius: "24px",
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      borderColor: "rgba(0, 212, 255, 0.3)",
                      boxShadow: "0 0 40px rgba(0, 212, 255, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 180,
                      height: 180,
                      background: "rgba(0, 212, 255, 0.05)",
                      border: "2px dashed rgba(0, 212, 255, 0.3)",
                      borderRadius: "20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <QrCode2Icon
                      sx={{ fontSize: 56, color: "#00d4ff", mb: 1 }}
                    />
                    <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                      小程序码
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: "#f8fafc" }}
                  >
                    微信扫码
                  </Typography>
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem" }}>
                    打开微信扫一扫，即可进入小程序
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <Box
                  sx={{
                    p: 5,
                    background: "rgba(18, 18, 26, 0.8)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    borderRadius: "24px",
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      borderColor: "rgba(124, 58, 237, 0.3)",
                      boxShadow: "0 0 40px rgba(124, 58, 237, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 180,
                      height: 180,
                      background:
                        "linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)",
                      borderRadius: "20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <SearchIcon
                      sx={{ fontSize: 56, color: "#7c3aed", mb: 1 }}
                    />
                    <Typography sx={{ color: "#a78bfa", fontWeight: 500 }}>
                      搜索"知嘛"
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: "#f8fafc" }}
                  >
                    微信搜索
                  </Typography>
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem" }}>
                    在微信中搜索"知嘛"小程序
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 8 }}>
            <Typography sx={{ color: "#64748b" }}>
              已有
              <Typography
                component="span"
                sx={{
                  mx: 0.5,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                100,000+
              </Typography>
              用户正在使用知嘛
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// 主页组件
function HomePage() {
  return (
    <Box>
      <HeroSection />
      <FeaturesSection />
      <ScenariosSection />
      <WhyChooseSection />
      <AboutSection />
      <DownloadSection />
    </Box>
  );
}

export default HomePage;
